
import Vue from "vue";
import TableCustom from "@/components/TableCustom.vue";
import colorStatus from "@/common/mixins/colorStatus";
import LockedIcon from "@/components/icons/LockedIcon.vue";
import DragHandleIcon from "@/components/icons/DragHandleIcon.vue";
import SensorIcon from "@/components/icons/SensorIcon.vue";
import { batteryStatusColor } from "@/common/constants/batteryStatus";

const STORAGE_KEY = "overall.rvsStatusByEquipment.collapsed";

export default Vue.extend({
  name: "RvsStatusByEquipment",
  mixins: [colorStatus],
  components: { TableCustom, LockedIcon, SensorIcon, DragHandleIcon },
  computed: {
    companyId(): any {
      return this.$store.getters.company.id || 0;
    },
    isNotUpgrade(): boolean {
      if (this.$store.state.user.role === "admin") return false;
      return !this.$store.state.company.rvs_upgrade;
    },
    statusList(): any[] {
      const list = [
        { name: "All", value: null, count: 0 },
        { name: "Normal", value: 0, count: 0 },
        { name: "Warning", value: 1, count: 0 },
        { name: "Alarm", value: 2, count: 0 },
        { name: "Offline", value: -1, count: 0 },
      ];
      (this.datas as any[]).forEach((d: any) => {
        list.forEach((s) => {
          if (s.value === d.overAllStatus || s.value === null) s.count++;
        });
      });
      return list;
    },
    totalSensorCount(): number {
      return (this.datas as any[]).reduce((sum: number, item: any) => sum + (item.sensors?.length || 0), 0);
    },
    filteredDatas(): any[] {
      if (this.selectedStatus === null) return this.datas;
      return this.datas.filter((row: any) => row.overAllStatus === this.selectedStatus);
    },
    isAllExpanded(): boolean {
      return (
        (this.datas as any[]).length > 0 &&
        (this.datas as any[]).every((d: any) => (this.expandedRows as Set<number>).has(d.equipmentId))
      );
    },
  },
  data() {
    const headerTable = [
      { text: "", value: "expand", sortable: false, hideLabel: true, width: "40px", padding: 0 },
      { text: "Machine", value: "machine", sortable: false },
      { text: "Fault Event", value: "faultEventCount", sortable: false, textAlign: "center", width: "100px" },
      { text: "Overall", value: "overAllStatus", sortable: false, textAlign: "center", width: "100px" },
      { text: "Failure Mode", value: "failureModeStatus", sortable: false, textAlign: "center", width: "100px" },
      { text: "Current Failure Mode", value: "failureModeDetails", sortable: false, padding: 0 },
      { text: "Battery", value: "battery", sortable: false, textAlign: "center", width: "80px" },
      { text: "To", value: "to", sortable: false, hideLabel: true, width: "24px" },
    ];
    return {
      datas: [] as any[],
      overallStatus: -1 as number,
      headerTable,
      selectedStatus: null as number | null,
      isLoading: false,
      expandedRows: new Set<number>(),
      isCollapsed: true,
    };
  },
  async created() {
    try {
      this.isCollapsed = window.localStorage.getItem(STORAGE_KEY) !== "open";
    } catch {
      this.isCollapsed = true;
    }
    await this.fetchOverallModel();
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    async fetchOverallModel() {
      if (!this.companyId) return;
      if (this.isNotUpgrade) return;
      this.isLoading = true;
      try {
        const raw: any[] = await Vue.prototype.$dep.modelUseCase.getOverallStatusRvsByEquipment(this.companyId);

        const filtered = raw
          .filter((item) => item.overAllStatus !== -2)
          .sort((a, b) => {
            if (b.overAllStatus !== a.overAllStatus) return b.overAllStatus - a.overAllStatus;
            return a.machine.localeCompare(b.machine);
          });

        this.datas = filtered;
        this.overallStatus = filtered.length ? Math.max(...filtered.map((v) => v.overAllStatus)) : -2;
      } catch (error) {
        console.error("Error fetching RVS overall status by equipment:", error);
      } finally {
        this.isLoading = false;
      }
    },
    status(statusVal: number) {
      const map: Record<number, { label: string; color: string; bgColor: string }> = {
        0: { label: "Normal", color: "normal", bgColor: "bg-[#2EC577]/[0.2]" },
        1: { label: "Warning", color: "warning", bgColor: "bg-[#FCA239]/[0.2]" },
        2: { label: "Alarm", color: "alarm", bgColor: "bg-[#D9263B]/[0.2]" },
        [-1]: { label: "Offline", color: "offline", bgColor: "bg-[#A5ACB3]/[0.2]" },
        [-2]: { label: "Disabled", color: "disabled", bgColor: "bg-[#808080]/[0.2]" },
      };
      return map[statusVal] || { label: "Disabled", color: "disabled", bgColor: "bg-[#808080]/[0.2]" };
    },
    sensorStatusColor(statusVal: number): string {
      const map: Record<number, string> = {
        0: "#2EC577",
        1: "#FCA239",
        2: "#D9263B",
        [-1]: "#A5ACB3",
      };
      return map[statusVal] ?? "#A5ACB3";
    },
    batteryColor(batteryStatus: number): string {
      // Delegates to the shared battery-status palette (adds Critical = #8B0000).
      // Note: crud_equipment_model_overall_summary.py coerces a NULL batteryStatus
      // to -1 upstream, so this still renders "Offline" rather than "No Data" for
      // missing data -- pre-existing behaviour, left unchanged here.
      return batteryStatusColor(batteryStatus);
    },
    toggleExpandAll() {
      if ((this as any).isAllExpanded) {
        (this as any).expandedRows = new Set();
      } else {
        (this as any).expandedRows = new Set(((this as any).datas as any[]).map((d: any) => d.equipmentId));
      }
    },
    toggleExpand(equipmentId: number) {
      const next = new Set(this.expandedRows);
      if (next.has(equipmentId)) {
        next.delete(equipmentId);
      } else {
        next.add(equipmentId);
      }
      this.expandedRows = next;
    },
    handleOpenMenu(modelId: any) {
      this.$router.push({ name: "ViewModelPage", params: { id: modelId } });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
  watch: {
    companyId() {
      this.fetchOverallModel();
    },
    isNotUpgrade() {
      this.fetchOverallModel();
    },
    isCollapsed(value: boolean) {
      try {
        window.localStorage.setItem(STORAGE_KEY, value ? "closed" : "open");
      } catch {
        /* localStorage unavailable -- ignore */
      }
    },
  },
});
